import { useState, useEffect } from 'react';
import {
  Clock,
  MapPin,
  User,
  Package,
  Calendar,
  Check,
  Loader2,
  Pencil,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Task, UpdateTaskDto } from '@/api/tasks';

interface TaskDetailSheetProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (id: string, updates: UpdateTaskDto) => void;
  onComplete: (id: string) => void;
  onCancel: (id: string) => void;
  isUpdating?: boolean;
  isCompleting?: boolean;
  isCancelling?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  INDOOR: 'У клиента',
  OUTDOOR: 'Внешняя',
  PERSONAL: 'Личная',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDeadline(deadline: string): { text: string; isOverdue: boolean; isToday: boolean } {
  const date = new Date(deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);

  const isOverdue = deadlineDate < today;
  const isToday = deadlineDate.getTime() === today.getTime();
  const isTomorrow = deadlineDate.getTime() === tomorrow.getTime();

  let text: string;
  if (isToday) {
    text = 'Сегодня';
  } else if (isTomorrow) {
    text = 'Завтра';
  } else if (isOverdue) {
    text = `Просрочено (${date.toLocaleDateString('ru', { day: 'numeric', month: 'short' })})`;
  } else {
    text = date.toLocaleDateString('ru', { day: 'numeric', month: 'long' });
  }

  return { text, isOverdue, isToday };
}

export function TaskDetailSheet({
  task,
  open,
  onOpenChange,
  onUpdate,
  onComplete,
  onCancel,
  isUpdating,
  isCompleting,
  isCancelling,
}: TaskDetailSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');

  // Reset form and dialogs when task changes or sheet opens/closes
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setDeadline(task.deadline ? task.deadline.split('T')[0] : '');
      setIsEditing(false);
    }
    // Always reset dialogs
    setCompleteDialogOpen(false);
    setCancelDialogOpen(false);
  }, [task, open]);

  if (!task) return null;

  const isPending = task.status === 'PENDING';
  const isLoading = isUpdating || isCompleting || isCancelling;

  const handleSave = () => {
    const updates: UpdateTaskDto = {};

    if (title.trim() !== task.title) {
      updates.title = title.trim();
    }
    if (description.trim() !== (task.description || '')) {
      updates.description = description.trim() || undefined;
    }
    if (deadline !== (task.deadline?.split('T')[0] || '')) {
      updates.deadline = deadline || undefined;
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(task.id, updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTitle(task.title);
    setDescription(task.description || '');
    setDeadline(task.deadline ? task.deadline.split('T')[0] : '');
    setIsEditing(false);
  };

  const handleCompleteTask = () => {
    onComplete(task.id);
    setCompleteDialogOpen(false);
    onOpenChange(false);
  };

  const handleCancelTask = () => {
    onCancel(task.id);
    setCancelDialogOpen(false);
    onOpenChange(false);
  };

  const deadlineInfo = task.deadline ? formatDeadline(task.deadline) : null;
  const TypeIcon = task.type === 'PERSONAL' ? User : Package;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" hideCloseButton>
          <SheetHeader>
            <SheetTitle className="text-left flex items-center gap-2">
              <div className={`p-1.5 rounded ${task.type === 'PERSONAL' ? 'bg-cyan-100' : 'bg-amber-100'}`}>
                <TypeIcon className={`h-4 w-4 ${task.type === 'PERSONAL' ? 'text-cyan-700' : 'text-amber-700'}`} />
              </div>
              <span className="truncate">
                {isEditing ? 'Редактирование' : task.title}
              </span>
              {!isEditing && isPending && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 ml-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditing) {
                    setIsEditing(false);
                  } else {
                    onOpenChange(false);
                  }
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Edit form */}
            {isEditing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Название</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Что нужно сделать?"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Описание</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Дополнительные детали..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Дедлайн</Label>
                  <Input
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleSave}
                    disabled={!title.trim() || isUpdating}
                  >
                    {isUpdating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Сохранить
                  </Button>
                  <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
                    <X className="h-4 w-4 mr-2" />
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* View mode */}
                {/* Description */}
                {task.description && (
                  <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                    {task.description}
                  </div>
                )}

                {/* Meta info */}
                <div className="space-y-2">
                  {/* Deadline */}
                  {deadlineInfo && (
                    <div className="flex items-center gap-2">
                      <Clock className={`h-4 w-4 ${deadlineInfo.isOverdue ? 'text-destructive' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${deadlineInfo.isOverdue ? 'text-destructive font-medium' : ''}`}>
                        {deadlineInfo.text}
                      </span>
                      {deadlineInfo.isOverdue && (
                        <Badge variant="destructive" className="text-xs">Просрочено</Badge>
                      )}
                      {deadlineInfo.isToday && (
                        <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">Сегодня</Badge>
                      )}
                    </div>
                  )}

                  {/* Client */}
                  {task.client && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{task.client.name}</span>
                    </div>
                  )}

                  {/* Type */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <TypeIcon className="h-4 w-4" />
                    <span>{TYPE_LABELS[task.type]}</span>
                  </div>

                  {/* Employee */}
                  {task.employee && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>
                        {task.employee.lastName} {task.employee.firstName}
                      </span>
                    </div>
                  )}

                  {/* Created date */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>Создано: {formatDate(task.createdAt)}</span>
                  </div>

                  {/* Completed date */}
                  {task.completedAt && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Check className="h-4 w-4" />
                      <span>Выполнено: {formatDate(task.completedAt)}</span>
                    </div>
                  )}
                </div>

                {/* Status badge - only for completed/cancelled */}
                {task.status !== 'PENDING' && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={task.status === 'COMPLETED' ? 'secondary' : 'destructive'}
                    >
                      {task.status === 'COMPLETED' ? 'Выполнено' : 'Отменено'}
                    </Badge>
                  </div>
                )}

                {/* Actions */}
                {isPending && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      className="flex-1"
                      onClick={() => setCompleteDialogOpen(true)}
                      disabled={isLoading}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Выполнить
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={isLoading}
                      className="text-muted-foreground"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Отменить задачу
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Complete confirmation */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Выполнить задачу?</AlertDialogTitle>
            <AlertDialogDescription>
              Задача "{task.title}" будет отмечена как выполненная.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Назад</AlertDialogCancel>
            <AlertDialogAction onClick={handleCompleteTask}>
              {isCompleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Выполнить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить задачу?</AlertDialogTitle>
            <AlertDialogDescription>
              Задача "{task.title}" будет отменена.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Назад</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelTask}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              Отменить задачу
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
